import { ACTION_CONFIGS } from '../model/action-configs';
import { BulkActionOption } from '../model/bulk-action-option.model';
import { LabelsBag } from '../model/bulk-subscription-column.model';
import { BOOLEAN_SUBSCRIPTION_KEYS } from '../model/subscription-keys';
import { SubscriptionTable } from '../model/subscription-table.model';
import { BASE_COLUMNS } from '../model/table-columns';
import { ACTION_PROPERTY_MAP } from '../model/action-property-map';
import { eventDescriptors } from '../model/event-descriptors';

export function createBulkActionOptions(
  actionType: string,
  valuePrefix: string,
  assignLabel: string,
  unassignLabel: string,
): BulkActionOption[] {
  const suffix = actionType === 'all' ? '-all' : '';
  return [
    { value: `${valuePrefix}-assign${suffix}`, viewValue: assignLabel },
    { value: `${valuePrefix}-unassign${suffix}`, viewValue: unassignLabel },
  ];
}

export function buildBulkActionsMap(labels: LabelsBag): Record<string, BulkActionOption[]> {
  const map: Record<string, BulkActionOption[]> = {};
  ACTION_CONFIGS.forEach((config) => {
    const propertyName = ACTION_PROPERTY_MAP[config.key];
    if (!propertyName) return;
    const assignLabel = config.labelProperty ? labels.paymentPush : labels.typeAssign;
    const unassignLabel = config.labelProperty ? labels.paymentPush : labels.typeUnassign;
    map[propertyName] = createBulkActionOptions(config.key, config.valuePrefix, assignLabel, unassignLabel);
  });
  return map;
}

export function filterSubscriptionTableByMode(list: SubscriptionTable[]): SubscriptionTable[] {
  return list.filter(
    (sub) =>
      sub.emailSubscriptionId ||
      sub.voiceSubscriptionId ||
      sub.smsSubscriptionId ||
      sub.pushSubscriptionId ||
      sub.offerSubscriptionId ||
      sub.performanceSubscriptionId ||
      sub.coachingEmailSubscriptionId ||
      sub.coachingVoiceSubscriptionId ||
      sub.coachingSmsSubscriptionId ||
      sub.coachingPushSubscriptionId ||
      sub.paymentEmailSubscriptionId ||
      sub.remindersEmailSubscriptionId ||
      sub.remindersPushSubscriptionId ||
      sub.alertRuleEmailSubscriptionId,
  );
}

export function computeSubscriptionChanges(
  currentSubscriptions: SubscriptionTable[],
  previousSubscriptions: SubscriptionTable[],
): Array<Record<string, unknown>> {
  const subscriptionChanges: Array<Record<string, unknown>> = [];

  currentSubscriptions.forEach((currentSubscription) => {
    const matchingPreviousSubscription = previousSubscriptions.find(
      (previousSubscription) => previousSubscription.tag === currentSubscription.tag,
    );

    if (!matchingPreviousSubscription) {
      return;
    }

    const hasDifferences = BOOLEAN_SUBSCRIPTION_KEYS.some(
      (subscriptionKey) =>
        (matchingPreviousSubscription as any)[subscriptionKey] !== (currentSubscription as any)[subscriptionKey],
    );

    if (!hasDifferences) {
      return;
    }

    const subscriptionDiff: Record<string, unknown> = {
      entityId: currentSubscription.entityId || (matchingPreviousSubscription as any).tag,
      entityType: matchingPreviousSubscription.entityType || 'SITE',
    };

    BOOLEAN_SUBSCRIPTION_KEYS.forEach((subscriptionKey) => {
      const subscriptionIdKey = `${subscriptionKey}SubscriptionId` as const;

      (subscriptionDiff as any)[subscriptionIdKey] =
        (currentSubscription as any)[subscriptionIdKey] || (matchingPreviousSubscription as any)[subscriptionIdKey];

      if ((matchingPreviousSubscription as any)[subscriptionKey] !== (currentSubscription as any)[subscriptionKey]) {
        (subscriptionDiff as any)[subscriptionKey] = (currentSubscription as any)[subscriptionKey] ? 'add' : 'del';
      }
    });

    subscriptionChanges.push(subscriptionDiff);
  });

  return subscriptionChanges;
}

export function getInitialEventsToEmit(subscriptionTable: SubscriptionTable[]): string[] {
  const remainingEvents = new Set(eventDescriptors.map((descriptor) => descriptor.event));
  const emittedEvents: string[] = [];

  for (let rowIndex = 0; rowIndex < subscriptionTable.length && remainingEvents.size > 0; rowIndex++) {
    const subscriptionRow = subscriptionTable[rowIndex];

    for (const descriptor of eventDescriptors) {
      const eventAlreadyPending = remainingEvents.has(descriptor.event);
      const rowHasProperty = (subscriptionRow as any)[descriptor.prop];

      if (eventAlreadyPending && rowHasProperty) {
        emittedEvents.push(descriptor.event);
        remainingEvents.delete(descriptor.event);
      }
    }
  }

  return emittedEvents;
}

export function subscriptionChanges(
  currentSubscriptions: SubscriptionTable[],
  previousSubscriptions: SubscriptionTable[],
): Array<Record<string, unknown>> {
  return computeSubscriptionChanges(currentSubscriptions, previousSubscriptions);
}

const PAYMENT_COLUMNS = ['site', 'paymentEmail', 'paymentPush'];

export function getBaseColumns(hasDRFinancialRole: boolean, hasOnlyDRFinancialRole = false): string[] {
  if (hasOnlyDRFinancialRole) {
    return PAYMENT_COLUMNS;
  }
  return BASE_COLUMNS.filter((col) => hasDRFinancialRole || (col !== 'paymentEmail' && col !== 'paymentPush'));
}
